import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Wraps an async refresh callback with guarding against concurrent calls,
 * unmounted-component updates, and propagates failures to the caller's
 * existing error UI (it never throws — callers keep their own try/catch).
 */
export const usePullRefresh = (onRefresh) => {
  const [refreshing, setRefreshing] = useState(false);
  const onRefreshRef = useRef(onRefresh);
  const isRefreshingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleRefresh = useCallback(() => {
    if (isRefreshingRef.current || typeof onRefreshRef.current !== "function") {
      return;
    }

    isRefreshingRef.current = true;
    setRefreshing(true);

    Promise.resolve()
      .then(() => onRefreshRef.current())
      .catch((error) => {
        console.error("[PullToRefresh] refresh callback failed:", error);
      })
      .finally(() => {
        isRefreshingRef.current = false;
        if (mountedRef.current) setRefreshing(false);
      });
  }, []);

  return { refreshing, handleRefresh };
};
